import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type {
  AdminAction, AdminAppeal, AdminChatReport, AdminCounts, AdminNotice, AdminStream, AdminSuspension, Block,
  CopyrightNoticeRequest, CounterNotice, RemovalReason, SafetyStatus,
} from '@zip2live/shared/safety';
import { ApiError } from './api.ts';
import { ensureSession } from './auth.ts';
import { setBlockedAuthors } from './chat.ts';
import { refreshMe } from './me.ts';
import { forgetAgreement } from './rules.ts';

/**
 * Safety on the web: blocking people, appeals, deleting your account, copyright notices and the
 * staff review queue. Blocks are per account (guests too) and follow you across devices.
 */

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/v1${path}`, { credentials: 'include', ...init, headers: { 'Content-Type': 'application/json', ...init.headers } });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (body as { error?: { code: string; message: string } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? 'Something went wrong. Try again.');
  }
  return body as T;
}

// ---- Blocks ----

interface BlockState {
  blocks: Block[];
  /** Per stream this page asked about: the block key if you blocked its creator, else null. */
  creators: Map<string, string | null>;
  loaded: boolean;
}
let state: BlockState = { blocks: [], creators: new Map(), loaded: false };
const listeners = new Set<() => void>();
const inflight = new Map<string, Promise<void>>();

function set(next: BlockState) {
  state = next;
  setBlockedAuthors(next.blocks.map((b) => b.key));
  for (const l of listeners) l();
}

async function loadBlocks(streamId?: string) {
  const r = await call<{ blocks: Block[]; creatorKey: string | null }>(`/me/blocks${streamId ? `?stream=${encodeURIComponent(streamId)}` : ''}`).catch(() => null);
  if (!r) return;
  const creators = new Map(state.creators);
  if (streamId) creators.set(streamId, r.creatorKey);
  set({ blocks: r.blocks, creators, loaded: true });
}

/** People you blocked; with a stream id, also whether you blocked its creator. */
export function useBlocks(streamId?: string) {
  const snap = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => void listeners.delete(cb);
    },
    () => state,
  );
  useEffect(() => {
    const key = streamId ?? '';
    if ((streamId ? !state.creators.has(streamId) : !state.loaded) && !inflight.has(key)) {
      inflight.set(key, loadBlocks(streamId).finally(() => inflight.delete(key)));
    }
  }, [streamId]);
  const creatorKey = streamId ? (snap.creators.get(streamId) ?? null) : null;
  return { blocks: snap.blocks, loaded: snap.loaded, creatorBlocked: creatorKey !== null, creatorKey };
}

/** Block a chat message's author (seq), or the stream's creator. */
export async function block(streamId: string, seq?: number) {
  await ensureSession();
  const b = await call<{ key: string; name: string }>('/me/blocks', { method: 'POST', body: JSON.stringify({ streamId, seq }) });
  const creators = new Map(state.creators);
  creators.delete(streamId);
  set({ ...state, blocks: [{ key: b.key, name: b.name, createdAt: Date.now() }, ...state.blocks.filter((x) => x.key !== b.key)], creators });
  await loadBlocks(streamId);
  return b;
}

export async function unblock(key: string, streamId?: string) {
  await call(`/me/blocks/${encodeURIComponent(key)}`, { method: 'DELETE' });
  set({ ...state, blocks: state.blocks.filter((b) => b.key !== key), creators: new Map() });
  if (streamId) await loadBlocks(streamId);
}

// ---- Your account: removals, suspension, appeals, deletion ----

export function useSafety() {
  const [safety, setSafety] = useState<SafetyStatus | null>(null);
  const reload = useCallback(() => call<SafetyStatus>('/me/safety').then(setSafety, () => setSafety({ staff: false, suspension: null, removals: [] })), []);
  useEffect(() => void reload(), [reload]);
  return { safety, reload };
}

export const appeal = (body: { streamId?: string; message: string; counterNotice?: CounterNotice }) =>
  call<{ ok: true }>('/me/appeals', { method: 'POST', body: JSON.stringify(body) });

export async function deleteAccount() {
  await call<{ ok: true }>('/me', { method: 'DELETE', body: JSON.stringify({ confirm: 'DELETE' }) });
  forgetAgreement();
  set({ blocks: [], creators: new Map(), loaded: false });
  await refreshMe();
}

export const fileCopyrightNotice = (body: CopyrightNoticeRequest) => call<{ ok: true; id: string }>('/copyright', { method: 'POST', body: JSON.stringify(body) });

// ---- Review queue (staff) ----

const post = <T>(path: string, body: unknown) => call<T>(path, { method: 'POST', body: JSON.stringify(body) });

export const adminApi = {
  counts: () => call<AdminCounts>('/admin/counts'),
  streams: () => call<{ streams: AdminStream[] }>('/admin/streams'),
  stream: (id: string) => call<AdminStream>(`/admin/streams/${encodeURIComponent(id)}`),
  decideStream: (id: string, action: 'remove' | 'keep' | 'restore', reason?: RemovalReason, note?: string) =>
    post<AdminStream>(`/admin/streams/${encodeURIComponent(id)}`, { action, reason, note: note || undefined }),
  chat: () => call<{ reports: AdminChatReport[] }>('/admin/chat'),
  decideChat: (streamId: string, seq: number, action: 'remove' | 'dismiss', note?: string) =>
    post(`/admin/chat/${encodeURIComponent(streamId)}/${seq}`, { action, note: note || undefined }),
  copyright: () => call<{ notices: AdminNotice[] }>('/admin/copyright'),
  decideNotice: (id: string, action: 'remove' | 'reject', note?: string) => post<AdminNotice>(`/admin/copyright/${encodeURIComponent(id)}`, { action, note: note || undefined }),
  appeals: () =>
    call<{ appeals: (AdminAppeal & { stream: AdminStream | null; suspension: { reason: string; since: number } | null; person: AdminSuspension['person'] | null })[] }>('/admin/appeals'),
  decideAppeal: (id: string, action: 'accept' | 'deny', note?: string) => post(`/admin/appeals/${encodeURIComponent(id)}`, { action, note: note || undefined }),
  suspensions: () => call<{ suspensions: AdminSuspension[] }>('/admin/suspensions'),
  suspend: (userId: string, reason: string) => post(`/admin/users/${encodeURIComponent(userId)}/suspension`, { reason }),
  unsuspend: (userId: string) => call(`/admin/users/${encodeURIComponent(userId)}/suspension`, { method: 'DELETE' }),
  log: () => call<{ actions: AdminAction[] }>('/admin/log'),
};
